import { DateTime, SystemZone } from 'luxon';
import type { Duration } from 'luxon';
import type { Address, PaymentMethod } from '@instamenu/data-access';
import type { CustomI18n } from '@instamenu/context-i18n';

type Timespan = {
  start: DateTime;
  end: DateTime;
};

export const dateFormats = {
  dateShorter: 'L/d/yy',
  dateShort: 'LLL. d, y',
  dateMedium: 'cccc, LLL. d',
  dateLong: 'cccc, LLL. d, y',
  dateTimeShort: 'L/d/y h:mm a',
  dateTimeLong: "cccc, L/d/yy 'at' h:mm:ss a",
  timeOnly: 'h:mm a',
};

const currencyFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2, // both minimum and maximum seem required for older Safari on iOS
  minimumFractionDigits: 2,
});

const currencyFormatNoCents = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0, // both minimum and maximum seem required for older Safari on iOS
  minimumFractionDigits: 0,
});

export const formatCurrency = (cents: number, options = {}) => {
  const opts = Object.assign({}, { cents: true, parens: false }, options);
  const abs = (opts.cents ? currencyFormat : currencyFormatNoCents).format(Math.abs(cents) / 100);
  return cents < 0 ? (opts.parens ? `(${abs})` : `-${abs}`) : abs;
};

export const formatTimespan = (timespan: Timespan, i18n: CustomI18n) => {
  const { start, end } = timespan;
  const formattedStart = i18n.locale ? start.setLocale(i18n.locale) : start;
  const formattedEnd = i18n.locale ? end.setLocale(i18n.locale) : end;

  const startStr = formattedStart.toFormat(dateFormats.dateLong);
  const endStr = formattedEnd.toFormat(dateFormats.dateLong);

  if (formattedStart.hasSame(formattedEnd, 'day')) {
    return startStr;
  }

  return i18n.t('dateRange', { start: startStr, end: endStr });
};

export const formatDate = (
  date: string | DateTime | null | undefined,
  format = dateFormats.dateShort,
  timeZone?: string,
  locale?: string,
) => {
  if (!date) return '';
  let obj = typeof date === 'string' ? DateTime.fromISO(date) : date;
  if (timeZone) {
    obj = obj.setZone(timeZone);
  }

  if (locale) {
    obj = obj.setLocale(locale);
  }

  const isSystemTimeZone = !timeZone || SystemZone.instance.name === obj.zone.name;
  return obj.toFormat(!isSystemTimeZone && format.endsWith('h:mm a') ? format + ' ZZZZ' : format);
};

export const formatHour = (hour: number) => {
  const shortHour = hour % 12;
  return (shortHour === 0 ? 12 : shortHour) + (hour < 12 || hour === 24 ? 'am' : 'pm');
};

// time is seconds since midnight
export const formatTime = (time: number) => {
  const hours = Math.floor(time / 60 / 60) % 12 || 12;
  const minutes = Math.floor((time / 60) % 60);
  return (
    hours + ':' + ((minutes < 10 ? '0' : '') + minutes) + (time / 60 / 60 < 12 ? ' AM' : ' PM')
  );
};

export type TimeSpan = {
  startTime?: number;
  endTime?: number;
};

export const formatTimeRange = (range: TimeSpan) => {
  const { startTime, endTime } = range;
  if (typeof startTime !== 'number' || typeof endTime !== 'number') return '';
  return formatTime(startTime) + ' to ' + formatTime(endTime);
};

export const formatPhone = (phone: string) => {
  if (typeof phone !== 'string') return '';
  const trimmed = phone?.replace(/^\+1/, '');
  if (trimmed.length !== 10) return phone;
  return `(${trimmed.substring(0, 3)}) ${trimmed.substring(3, 6)}-${trimmed.substring(6, 10)}`;
};

export const sentenceCase = (str: string) => (str ? str[0].toUpperCase() + str.slice(1) : '');

const metersPerMile = 1609.34;
const metersPerFoot = 0.3048;

export const formatDistance = (meters: number) => {
  return meters > 10 * metersPerMile
    ? `${Math.round(meters / metersPerMile)} mi`
    : meters > 500 * metersPerFoot
      ? `${(meters / metersPerMile).toFixed(1)} mi`
      : `${Math.round(meters / metersPerFoot)} ft`;
};

export const formatAddress = (
  address?:
    | (Pick<Address, 'street' | 'city' | 'state' | 'zip'> & { street2?: string | null })
    | null,
  options?: { short: boolean },
) => {
  const opts = Object.assign({}, { short: false }, options);
  return address
    ? [
        address.street,
        address.street2,
        address.city,
        address.state,
        opts.short ? null : address.zip,
      ]
        .filter((x) => !!x)
        .join(', ')
    : null;
};

export const formatDuration = (duration: Duration, options?: { seconds: boolean }) => {
  const opts = Object.assign({}, { seconds: false }, options);
  if (!duration) return '';
  const ms = duration.toMillis();
  const s = Math.floor(Math.abs(ms) / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const fh = h > 0 ? h + 'h ' : '';
  const fm = h > 0 || m > 0 || !opts.seconds ? (m % 60) + 'm' + (opts.seconds ? ' ' : '') : '';
  const fs = opts.seconds ? (s % 60) + 's' : '';
  return (ms < 0 ? '- ' : '') + fh + fm + fs;
};

export function formatPaymentMethod(payment: Partial<PaymentMethod>, card: string) {
  return (
    (payment.brand === 'amex'
      ? 'American Express'
      : payment.brand === 'mastercard'
        ? 'MasterCard'
        : payment.brand
          ? payment.brand.slice(0, 1).toUpperCase() + payment.brand.slice(1)
          : card) + (payment.last4 ? ' •• ' + payment.last4 : '')
  );
}

export function roundTo(num: number, precision: number) {
  return Math.round(num / precision) * precision;
}

export function roundUpTo(num: number, precision: number) {
  return Math.ceil(num / precision) * precision;
}

// truncate a string to a length at the nearest space
export function truncate(str: string, length: number) {
  if (str.length <= length) return str;
  const i = str.lastIndexOf(' ', length);
  return str.slice(0, i > 0 ? i : length) + '…';
}
